/* Progressive enhancement. Product add, cart update and checkout remain native Shopify forms. */
(() => {
  'use strict';
  const query = (selector, root = document) => root.querySelector(selector);
  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const pendingVariants = new WeakMap();

  function initialize(root = document) {
    all('[data-variant-id]', root).forEach(input => { input.disabled = false; });
    all('[data-quantity]', root).forEach(syncQuantity);
    all('[data-product-root]', root).forEach(product => {
      if (!product.dataset.initialized) product.dataset.initialized = 'true';
    });
  }

  function syncQuantity(control) {
    const input = query('input', control);
    if (!input) return;
    const value = Number(input.value);
    const min = input.min === '' ? 1 : Number(input.min);
    const max = input.max === '' ? Infinity : Number(input.max);
    const minus = query('[data-quantity-change="-1"]', control);
    const plus = query('[data-quantity-change="1"]', control);
    if (minus) minus.disabled = Number.isFinite(value) && value <= min;
    if (plus) plus.disabled = Number.isFinite(value) && value >= max;
  }

  function showMedia(root, mediaId) {
    all('[data-media-id]', root).forEach(media => {
      const active = media.dataset.mediaId === String(mediaId);
      media.hidden = !active;
      if (!active) all('video', media).forEach(video => video.pause());
      if (!active) all('iframe', media).forEach(frame => {
        // Reload a hidden external player to stop playback without postMessage to unknown origins.
        if (frame.dataset.activePlayer === 'true') {
          const src = frame.src;
          frame.src = src;
          delete frame.dataset.activePlayer;
        }
      });
      if (active) all('iframe', media).forEach(frame => { frame.dataset.activePlayer = 'true'; });
    });
    all('[data-thumbnail]', root).forEach(button => button.setAttribute('aria-pressed', String(button.dataset.thumbnail === String(mediaId))));
  }

  async function changeVariant(select) {
    const root = select.closest('[data-product-root]');
    if (!root) return;
    const oldRequest = pendingVariants.get(root);
    if (oldRequest) oldRequest.abort();
    const controller = new AbortController();
    pendingVariants.set(root, controller);
    const info = query('[data-product-info]', root);
    const mengeFeld = query('[name="quantity"]', info);
    const quantity = mengeFeld ? mengeFeld.value : undefined;
    const focusId = select.id;
    all('[data-add-button],.shopify-payment-button button', info).forEach(button => { button.disabled = true; });
    const dynamicCheckout = query('.shopify-payment-button', info);
    if (dynamicCheckout) dynamicCheckout.hidden = true;
    info.setAttribute('aria-busy', 'true');
    const status = query('[data-variant-status]', info);
    if (status) status.textContent = '';
    try {
      const url = new URL(root.dataset.productUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Unexpected product origin');
      url.searchParams.set('section_id', root.dataset.sectionId);
      url.searchParams.set('option_values', all('[data-option-select]', root).map(option => option.value).join(','));
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Variant response failed');
      const html = await response.text();
      if (pendingVariants.get(root) !== controller) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const freshRoot = query('[data-product-root]', parsed);
      const freshInfo = freshRoot && query('[data-product-info]', freshRoot);
      if (!freshInfo || !query('[data-variant-id]', freshInfo)) throw new Error('Missing variant content');
      // Server-rendered option state supports product variants without a 250-variant JSON dump.
      info.replaceWith(freshInfo);
      initialize(freshInfo);
      const newQuantity = query('[name="quantity"]', freshInfo);
      if (newQuantity && quantity) {
        const min = Number(newQuantity.min || 1), step = Number(newQuantity.step || 1);
        const max = newQuantity.max ? Number(newQuantity.max) : Infinity;
        newQuantity.value = String(Math.min(max, Math.max(min, min + Math.floor((Number(quantity) - min) / step) * step)));
        syncQuantity(newQuantity.closest('[data-quantity]'));
      }
      const activeMedia = query('[data-media-id]:not([hidden])', freshRoot);
      if (activeMedia) showMedia(root, activeMedia.dataset.mediaId);
      const variantId = query('[data-variant-id]', freshInfo).value;
      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.delete('option_values');
      if (variantId) pageUrl.searchParams.set('variant', variantId);
      else { pageUrl.searchParams.delete('variant'); pageUrl.searchParams.set('option_values', url.searchParams.get('option_values')); }
      window.history.replaceState({}, '', pageUrl);
      const focusTarget = document.getElementById(focusId);
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      if (window.Shopify && window.Shopify.PaymentButton && window.Shopify.PaymentButton.init) window.Shopify.PaymentButton.init();
      document.dispatchEvent(new CustomEvent('zinke:variant:change', { detail: { variantId, sectionId: root.dataset.sectionId } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Keep purchasing disabled after a failed switch: never add the previously selected variant.
      if (status) { status.textContent = root.dataset.errorMessage; status.dataset.error = 'true'; }
    } finally {
      if (pendingVariants.get(root) === controller) { info.removeAttribute('aria-busy'); pendingVariants.delete(root); }
    }
  }

  document.addEventListener('click', event => {
    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const control = quantityButton.closest('[data-quantity]');
      const input = query('input', control);
      try { if (Number(quantityButton.dataset.quantityChange) > 0) input.stepUp(); else input.stepDown(); } catch { return; }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const thumbnail = event.target.closest('[data-thumbnail]');
    if (thumbnail) showMedia(thumbnail.closest('[data-gallery]'), thumbnail.dataset.thumbnail);
    all('[data-disclosure][open]').forEach(details => { if (!details.contains(event.target)) details.open = false; });
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-option-select]')) changeVariant(event.target);
    const quantity = event.target.closest('[data-quantity]');
    if (quantity) syncQuantity(quantity);
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const open = all('[data-disclosure][open]');
    if (open.length) { const last = open[open.length - 1]; last.open = false; const kopf = query('summary', last); if (kopf) kopf.focus(); }
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
})();
